import React, { useState } from "react";
import { KitProductModel } from "../../../Api/Models/product.model";
import ImageInput from "../../../Components/ImageInput";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "../../../Components/Icons/CheckIcon";
import DotBreadcrumbs from "../../../Components/DotBreadcrumbs";
import {useUser} from "../../../Context/UserProvider";
import { stockService } from "../../../Api/Services/StockService";

interface ConfrimationScreenProps {
  kitProducts: KitProductModel[];
  uncheckProduct(productId: number): void;
  setItemPrepared(productId: number): void;
  assignKit(files: File[], boxKeys: { [productId: number]: string }): Promise<void>;
  back: CallableFunction;
  currentStep: number;
  totalSteps: number;
}

const EditIcon: React.FunctionComponent<{}> = () => {
  return (
      <svg
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          width="20px"
      >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
        />
      </svg>
  );
};

const ConfrimationScreen: React.FunctionComponent<ConfrimationScreenProps> = ({
                                                                                kitProducts,
                                                                                uncheckProduct,
                                                                                setItemPrepared,
                                                                                assignKit,
                                                                                back,
                                                                                currentStep,
                                                                                totalSteps
                                                                              }) => {
  const [images, setImages] = useState<File[]>([]);
  const { t } = useTranslation();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const { user } = useUser();

  const [boxKeys, setBoxKeys] = useState<{ [productId: number]: string }>({});
  const [boxKeyErrors, setBoxKeyErrors] = useState<{ [productId: number]: string | null }>({});
  const [tempBoxKey, setTempBoxKey] = useState<string>("");


  const [showBoxKeyModal, setShowBoxKeyModal] = useState(false);
  const [currentBoxProductId, setCurrentBoxProductId] = useState<number | null>(null);
  const [currentBoxProductRealId, setCurrentBoxProductRealId] = useState<number | null>(null);


    const missingRequiredBoxKey = kitProducts.some(
      (p) => p.box && p.stockEnough && !p.skipped && p.selected && !boxKeys[p.id]
  );


    const openBoxKeyModal = (productId: number, productRealId: number) => {
        setCurrentBoxProductId(productId);
        setCurrentBoxProductRealId(productRealId);
        setTempBoxKey(boxKeys[productId] || "");
        setShowBoxKeyModal(true);
    };
  const validateTempBoxKey = async (productId: number) => {
    const value = tempBoxKey;

    if (!value) {
      setBoxKeyErrors(prev => ({ ...prev, [productId]: t("Key is required") }));
      return false;
    }

    try {
      const data = await stockService.validateBoxKey(currentBoxProductRealId!, value);

      if (!data.valid) {
        setBoxKeyErrors(prev => ({ ...prev, [productId]: t("Invalid key") }));
        return false;
      }

      setBoxKeyErrors(prev => ({ ...prev, [productId]: null }));
      return true;

    } catch (error) {
      setBoxKeyErrors(prev => ({ ...prev, [productId]: t("Validation failed") }));
      return false;
    }
  };



  const saveBoxKey = async () => {
    if (!currentBoxProductId) return;

    const valid = await validateTempBoxKey(currentBoxProductId);
    if (!valid) return;

    setBoxKeys(prev => ({
      ...prev,
      [currentBoxProductId]: tempBoxKey
    }));

    setShowBoxKeyModal(false);
  };

  const editProduct = (productId: number) => {
    uncheckProduct(productId);
  };

  /*const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Disable the button
    setButtonDisabled(true);

    // Enable the button after 300ms
    setTimeout(() => {
      setButtonDisabled(false);
    }, 1000);
    assignKit(images);
  };*/

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (buttonDisabled) return;

    setButtonDisabled(true);

    try {
      await assignKit(images, boxKeys);
    } finally {
      setButtonDisabled(false);
    }
  };

  const isFormValid = (): boolean => {
    if (!user?.skipAllGear) {
      return kitProducts.some((x) => x.selected);
    }
    return true;
  };


  return (
      <form onSubmit={handleSubmit}>

        <div className="separator-line"></div>
        <div className="heading mb-4">
          {t("Confirm items")}
        </div>

        {kitProducts
            .filter(
                (x) =>
                    x.selected || (!x.stockEnough && !x.optionalGroupId) || x.skipped
            )
            .map((product) => (
                <>
                  <div key={product.id}
                       className={"product-list-card mb-4 " + (product.stockEnough && !product.skipped && product.prepared ? " marked" : "") + (!product.stockEnough || product.skipped ? " disabled" : "")}>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <div className="product-list-card__image">
                          <img src={product.image} alt={product.name}/>
                        </div>
                      </div>

                      <div className="w-full">
                        <div className="product-list-card__title">
                          {Array.from({length: product.quantity}).map((_, i) => (
                              <React.Fragment key={i}>
                                <span>{product.name}</span>
                                {product.selectedVariationId && (
                                    <span className="">
                                      &nbsp;-&nbsp;
                                      {
                                        product.variations.find(
                                            (x) => x.id === product.selectedVariationId
                                        )?.name
                                      }
                                    </span>
                                )}
                                {product.box && boxKeys[product.id] && (
                                    <span>
                                      &nbsp;-&nbsp;{boxKeys[product.id]}
                                    </span>
                                )}
                                {product.box && product.stockEnough && !product.skipped && (
                                    <button
                                        type="button"
                                        className={`button button--small ${
                                            product.stockEnough && !product.skipped && product.prepared ? "button--light" : ""
                                        }`}
                                        onClick={() => openBoxKeyModal(product.id, product.productId)}
                                    >
                                      {boxKeys[product.id] ? t("Edit reference") : t("Set reference")}
                                    </button>
                                )}
                                {i !== product.quantity - 1 ? <br/> : null}
                              </React.Fragment>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-2 items-center">
                        {(product.variations.length > 0 || product.optionalGroupId) && (
                            <span onClick={() => editProduct(product.id)}>
                              <EditIcon/>
                            </span>
                        )}


                        {product.stockEnough && !product.skipped && (
                            <button
                                type="button"
                                className={`product-list-card__check needtocheck`}
                                onClick={() => setItemPrepared(product.id)}
                            >
                              {product.prepared && <CheckIcon />}
                            </button>
                        )}


                        {product.stockEnough && product.skipped && (
                            <div className="product-list-card__check"></div>
                        )}

                        {!product.stockEnough && (
                            /*<TimeIcon />*/
                            <div className="product-list-card__out-of-stock">
                              {t("Out of stock")}
                            </div>
                        )}

                      </div>

                    </div>
                  </div>
                </>
            ))}

        <div className="mt-8 mb-8">
          <DotBreadcrumbs currentStep={totalSteps} totalSteps={totalSteps}/>
        </div>

        <div className="mb-4">
          <ImageInput
              images={images}
              setImages={setImages}
              required={kitProducts.some((x) => x.stockEnough && !x.skipped)}
          />
        </div>


        <div className="flex gap-4 pb-4">
          <div className="flex-1">
            <button
                type="button"
                className="button button--light mr-3"
                onClick={() => back()}
            >
              {t("Back")}
            </button>
          </div>
          <div className="flex-1">
            <button
                type="submit"
                className={
                    "button " +
                    (
                        kitProducts.some((x) => x.selected && !x.prepared) ||
                        missingRequiredBoxKey
                            ? "button--disabled"
                            : ""
                    )
                }
                disabled={
                    buttonDisabled ||
                    kitProducts.some((x) => x.selected && !x.prepared) ||
                    missingRequiredBoxKey ||
                    !isFormValid()
                }
            >
              {t("Assign Item(s)")}
            </button>
          </div>
        </div>
        {showBoxKeyModal && (
            <div className="info-modal modal" tabIndex={-1} role="dialog">
              <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">

                <h3 className="text-lg font-semibold mb-4 text-center">
                  {t("Set Reference")}
                </h3>

                <input
                    className="input input--text input-field w-full"
                    value={tempBoxKey}
                    onChange={(e) => setTempBoxKey(e.target.value)}
                    onBlur={() => validateTempBoxKey(currentBoxProductId!)}
                    placeholder="Enter reference"
                />

                {boxKeyErrors[currentBoxProductId!] && (
                    <div className="text-red-500 text-sm mt-1">
                      {boxKeyErrors[currentBoxProductId!]}
                    </div>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <button type="button" className="button" onClick={saveBoxKey}>
                    {t("Save")}
                  </button>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                      type="button"
                      className="button button--light"
                      onClick={() => setShowBoxKeyModal(false)}
                  >
                    {t("Cancel")}
                  </button>

                </div>
              </div>
            </div>
        )}

      </form>
  );
};

export default ConfrimationScreen;
